import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_OPSERVICE_BASE_URL
const TEST_TELEGRAM_ID = parseInt(import.meta.env.VITE_TEST_TELEGRAM_ID || '865351408', 10)

interface AuthResponse {
  token: string
  user: {
    id: string
    telegram_id: number
    username?: string
    first_name?: string
    last_name?: string
    language_code?: string
  }
  expires_in: number
  test_mode?: boolean
}

/**
 * Authenticate with Telegram Mini App initData
 * @returns Authentication response with JWT token and user data
 * @throws Error if authentication fails or if not running in Telegram
 */
export const authenticateWithTelegram = async (): Promise<AuthResponse> => {
  const initData = window.Telegram?.WebApp?.initData

  // If Telegram WebApp is available with initData, use production auth
  if (window.Telegram?.WebApp && initData) {
    console.log('[Auth] Authenticating with Telegram initData...')

    try {
      const response = await axios.post<AuthResponse>(`${API_BASE_URL}/api/v1/auth/miniapp`, {
        init_data: initData,
      })

      console.log('[Auth] ✅ Authentication successful:', response.data.user)

      // Store JWT token in localStorage
      localStorage.setItem('jwt_token', response.data.token)
      localStorage.setItem('token_expires_at', String(Date.now() + response.data.expires_in * 1000))
      localStorage.removeItem('test_telegram_id')

      return response.data
    } catch (error) {
      console.error('[Auth] ❌ Telegram authentication failed:', error)
      throw error
    }
  }

  // Telegram not available - try test-auth (backend controls if allowed)
  // SECURITY: Backend returns 403 if APP_ENV=production, so this is safe
  console.log('[Auth] 🔧 Telegram WebApp not available, trying test authentication...')

  try {
    const response = await axios.post<AuthResponse>(`${API_BASE_URL}/api/v1/users/test-auth`, {
      telegram_id: TEST_TELEGRAM_ID,
    })

    console.log('[Auth] ✅ Test authentication successful:', response.data.user)

    localStorage.setItem('jwt_token', response.data.token)
    localStorage.setItem('token_expires_at', String(Date.now() + response.data.expires_in * 1000))
    localStorage.setItem('test_telegram_id', String(response.data.user.telegram_id))

    return response.data
  } catch (error: unknown) {
    // If backend rejected test-auth (403), it means we're in production
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      console.error('[Auth] ❌ Test mode disabled on server - Telegram WebApp required')
      throw new Error('Приложение доступно только через Telegram')
    }
    console.error('[Auth] ❌ Test authentication failed:', error)
    throw error
  }
}

/**
 * Get JWT token from localStorage
 */
export const getToken = (): string | null => {
  return localStorage.getItem('jwt_token')
}

/**
 * Check if token is expired
 */
export const isTokenExpired = (): boolean => {
  const expiresAt = localStorage.getItem('token_expires_at')
  if (!expiresAt) {
    return true
  }

  return Date.now() >= parseInt(expiresAt, 10)
}

/**
 * Clear authentication data
 */
export const clearAuth = () => {
  localStorage.removeItem('jwt_token')
  localStorage.removeItem('token_expires_at')
  localStorage.removeItem('test_telegram_id')
}

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = (): boolean => {
  const token = getToken()
  return !!token && !isTokenExpired()
}
